import { useState, useEffect, useCallback, useRef, memo } from "react"
import { MoodFrame } from "./AppleEmoji"
import { homeHour } from "@/config/time"
import { useAmbientMotion } from "@/components/AmbientMotion"
import {
  BASE_PIXELS,
  EYES,
  EYES_CLOSED,
  EYE_COLOR,
  SPRITE_SIZE,
  STRANDS,
  strandShift,
} from "./bitboySprite"

// ============================================================================
// Constants — CSS pixels for the 96px head.
// ============================================================================

/** Max cursor-tilt rotation (deg). */
const MAX_ROTATION = 5

/** Max cursor-follow translate X (px). */
const MAX_TX = 5

/** Max cursor-follow translate Y (px). Kept smaller — vertical tilt reads weaker. */
const MAX_TY = 3

/**
 * Distance (px) at which cursor-effect reaches ~63% of max — the time constant
 * of the exponential saturation. Nothing is ever clamped; the effect just
 * asymptotes smoothly as the cursor moves farther away.
 */
const SATURATION_DIST = 400

const THOUGHT_DURATION = 4000
const THOUGHT_PAUSE = 2000

/** How often the ASCII animation advances to its next frame (ms). */
const FRAME_INTERVAL = 280

interface AsciiMood {
  /**
   * Frame sequence looped while the bubble is visible. All frames in a
   * single mood are padded to the same width so the bubble doesn't jitter
   * as the animation advances (the bubble's width follows its content).
   */
  readonly frames: readonly string[]
}

// ---- ASCII mood library ----------------------------------------------------
// Keep frames short (≤5 cols), monospaced, and width-stable. The bubble
// chrome stays fixed — only the glyph sequence changes.

/** Sleep — z's accumulate then soft-reset. */
const MOOD_SLEEP: AsciiMood = {
  frames: ["·  ", "z  ", "zZ ", "zZz", "ZzZ", "zZ "],
}

/** Coffee cup with rising steam. */
const MOOD_COFFEE: AsciiMood = {
  frames: ["( ) ", "(·) ", "(~) ", "(≈) ", "(~) "],
}

/** Classic terminal spinner. */
const MOOD_SPIN: AsciiMood = {
  frames: [" | ", " / ", " - ", " \\ "],
}

/** Sun cresting the horizon. */
const MOOD_DAWN: AsciiMood = {
  frames: ["  .  ", " .-· ", "·-*-·", " '-·' "],
}

/** Noodle / steam wisp drifting past. */
const MOOD_NOODLE: AsciiMood = {
  frames: ["~··", "·~·", "··~", "·~·"],
}

/** Music notes bobbing. */
const MOOD_MUSIC: AsciiMood = {
  frames: ["♪  ", " ♫ ", "  ♪", " ♫ "],
}

/** Typing prompt — cursor blink. */
const MOOD_TYPE: AsciiMood = {
  frames: [">_ ", ">  ", ">_ ", ">  "],
}

/** Sun pulse. */
const MOOD_SUN: AsciiMood = {
  frames: [" · ", " * ", "*+*", " * "],
}

/** Moon phases (new → crescent → waning). */
const MOOD_MOON: AsciiMood = {
  frames: ["(  ", " c ", " C ", "  )", " C "],
}

/** Loading ellipsis. */
const MOOD_DOTS: AsciiMood = {
  frames: [".  ", ".. ", "...", ".. "],
}

/** Mini progress bar. */
const MOOD_PROGRESS: AsciiMood = {
  frames: ["[   ]", "[=  ]", "[== ]", "[===]", "[== ]", "[=  ]"],
}

/** Code braces breathing. */
const MOOD_CODE: AsciiMood = {
  frames: ["{ }", "{.}", "{ }", "{_}"],
}

/** Soft heart pulse. */
const MOOD_HEART: AsciiMood = {
  frames: [" · ", " ♥ ", "♥♥ ", " ♥ "],
}

/** Friendly wave. */
const MOOD_WAVE: AsciiMood = {
  frames: ["o/ ", "o- ", "o\\ ", "o- "],
}

/** Light rain. */
const MOOD_RAIN: AsciiMood = {
  frames: ["'  ", " ' ", "  '", " ' "],
}

/** Idea spark. */
const MOOD_IDEA: AsciiMood = {
  frames: [" · ", " . ", " * ", " *+", " * "],
}

/** Signal bars climbing. */
const MOOD_SIGNAL: AsciiMood = {
  frames: [".  ", ":  ", ":· ", ":= "],
}

/** Checkmark settle — task done. */
const MOOD_CHECK: AsciiMood = {
  frames: ["[ ]", "[.]", "[v]", "[v]"],
}

/** Ship / deploy drifting right. */
const MOOD_SHIP: AsciiMood = {
  frames: ["·>  ", " ·> ", "  ·>", " ·> "],
}

/** Book pages fluttering. */
const MOOD_BOOK: AsciiMood = {
  frames: ["|=|", "|≈|", "|=|", "|≈|"],
}

/** Soft sparkle pair. */
const MOOD_SPARK: AsciiMood = {
  frames: ["· *", "* ·", " ·*", "*· "],
}

// ---- Emoji mood variants (additive — same bubble chrome) --------------------
// Single-emoji (or short) frames keep the chip compact. Sequences still
// share a fixed width so the bubble doesn't jitter between frames.

/** Sleepy face blink. */
const MOOD_EMOJI_SLEEP: AsciiMood = {
  frames: ["😴", "😪", "😴", "💤"],
}

/** Morning coffee. */
const MOOD_EMOJI_COFFEE: AsciiMood = {
  frames: ["☕", "☕", "♨️", "☕"],
}

/** Sunrise / good morning. */
const MOOD_EMOJI_DAWN: AsciiMood = {
  frames: ["🌅", "🌄", "☀️", "🌅"],
}

/** Lunch / noodles. */
const MOOD_EMOJI_FOOD: AsciiMood = {
  frames: ["🍜", "🍲", "🍛", "🍜"],
}

/** Music vibes. */
const MOOD_EMOJI_MUSIC: AsciiMood = {
  frames: ["🎵", "🎶", "🎧", "🎵"],
}

/** Focus / deep work. */
const MOOD_EMOJI_FOCUS: AsciiMood = {
  frames: ["💻", "⌨️", "🖥️", "💻"],
}

/** Sunny afternoon. */
const MOOD_EMOJI_SUN: AsciiMood = {
  frames: ["☀️", "🌤️", "☀️", "😎"],
}

/** Night sky. */
const MOOD_EMOJI_MOON: AsciiMood = {
  frames: ["🌙", "🌕", "🌙", "✨"],
}

/** Friendly wave. */
const MOOD_EMOJI_WAVE: AsciiMood = {
  frames: ["👋", "✋", "👋", "🤚"],
}

/** Idea spark. */
const MOOD_EMOJI_IDEA: AsciiMood = {
  frames: ["💡", "✨", "💡", "💭"],
}

/** Ship it. */
const MOOD_EMOJI_SHIP: AsciiMood = {
  frames: ["🚀", "🚀", "✨", "🚀"],
}

/** Reading. */
const MOOD_EMOJI_BOOK: AsciiMood = {
  frames: ["📖", "📚", "📖", "👀"],
}

/** Rainy night. */
const MOOD_EMOJI_RAIN: AsciiMood = {
  frames: ["🌧️", "💧", "🌧️", "☔"],
}

/** Done / check. */
const MOOD_EMOJI_CHECK: AsciiMood = {
  frames: ["✅", "☑️", "✅", "🎉"],
}

/** Heart. */
const MOOD_EMOJI_HEART: AsciiMood = {
  frames: ["💛", "🧡", "❤️", "💛"],
}

interface MoodSlot {
  /** Inclusive start hour (0–23, Bangkok local). */
  readonly start: number
  /** Exclusive end hour. */
  readonly end: number
  /** Pool of animations this slot picks from, uniformly at random. */
  readonly moods: readonly AsciiMood[]
}

/**
 * Time-of-day mood pools. Slots are non-overlapping and cover 0–24. Night
 * slots are the ones that include MOOD_SLEEP — that's the single source of
 * truth for when the eyes stay closed (see isBangkokNight).
 */
const MOOD_SLOTS: readonly MoodSlot[] = [
  // late night
  {
    start: 0,
    end: 5,
    moods: [
      MOOD_SLEEP,
      MOOD_MOON,
      MOOD_RAIN,
      MOOD_SPARK,
      MOOD_EMOJI_SLEEP,
      MOOD_EMOJI_MOON,
      MOOD_EMOJI_RAIN,
    ],
  },
  // dawn
  {
    start: 5,
    end: 7,
    moods: [
      MOOD_DAWN,
      MOOD_COFFEE,
      MOOD_SUN,
      MOOD_IDEA,
      MOOD_EMOJI_DAWN,
      MOOD_EMOJI_COFFEE,
      MOOD_EMOJI_IDEA,
    ],
  },
  // morning
  {
    start: 7,
    end: 10,
    moods: [
      MOOD_COFFEE,
      MOOD_SUN,
      MOOD_WAVE,
      MOOD_TYPE,
      MOOD_DOTS,
      MOOD_EMOJI_COFFEE,
      MOOD_EMOJI_SUN,
      MOOD_EMOJI_WAVE,
    ],
  },
  // deep work
  {
    start: 10,
    end: 12,
    moods: [
      MOOD_SPIN,
      MOOD_TYPE,
      MOOD_CODE,
      MOOD_PROGRESS,
      MOOD_COFFEE,
      MOOD_SIGNAL,
      MOOD_EMOJI_FOCUS,
      MOOD_EMOJI_COFFEE,
      MOOD_EMOJI_CHECK,
    ],
  },
  // lunch
  {
    start: 12,
    end: 14,
    moods: [
      MOOD_NOODLE,
      MOOD_HEART,
      MOOD_WAVE,
      MOOD_MUSIC,
      MOOD_EMOJI_FOOD,
      MOOD_EMOJI_HEART,
      MOOD_EMOJI_WAVE,
    ],
  },
  // afternoon
  {
    start: 14,
    end: 17,
    moods: [
      MOOD_COFFEE,
      MOOD_SUN,
      MOOD_MUSIC,
      MOOD_CODE,
      MOOD_SHIP,
      MOOD_CHECK,
      MOOD_EMOJI_SUN,
      MOOD_EMOJI_SHIP,
      MOOD_EMOJI_CHECK,
      MOOD_EMOJI_FOCUS,
    ],
  },
  // dinner / dusk
  {
    start: 17,
    end: 20,
    moods: [
      MOOD_NOODLE,
      MOOD_SUN,
      MOOD_MUSIC,
      MOOD_BOOK,
      MOOD_HEART,
      MOOD_EMOJI_FOOD,
      MOOD_EMOJI_MUSIC,
      MOOD_EMOJI_BOOK,
      MOOD_EMOJI_HEART,
    ],
  },
  // evening
  {
    start: 20,
    end: 23,
    moods: [
      MOOD_MUSIC,
      MOOD_MOON,
      MOOD_TYPE,
      MOOD_SPARK,
      MOOD_IDEA,
      MOOD_BOOK,
      MOOD_EMOJI_MUSIC,
      MOOD_EMOJI_MOON,
      MOOD_EMOJI_IDEA,
      MOOD_EMOJI_BOOK,
    ],
  },
  // late
  {
    start: 23,
    end: 24,
    moods: [
      MOOD_SLEEP,
      MOOD_MOON,
      MOOD_RAIN,
      MOOD_DOTS,
      MOOD_EMOJI_SLEEP,
      MOOD_EMOJI_MOON,
      MOOD_EMOJI_RAIN,
    ],
  },
]

/**
 * Pick a random mood from the slot covering the current Bangkok hour.
 * When `exclude` is set, retries a few times so consecutive cycles feel
 * varied (pools are small enough that a hard guarantee isn't worth it).
 */
function pickMood(exclude: AsciiMood | null = null): AsciiMood {
  const hour = homeHour()
  const slot =
    MOOD_SLOTS.find((s) => hour >= s.start && hour < s.end) ?? MOOD_SLOTS[0]!
  const pool = slot.moods
  if (pool.length === 1) return pool[0]!

  let next = pool[Math.floor(Math.random() * pool.length)]!
  for (let i = 0; i < 4 && next === exclude; i++) {
    next = pool[Math.floor(Math.random() * pool.length)]!
  }
  return next
}

/**
 * True when BitBoy should be "asleep" — his eyes stay closed instead of
 * blinking periodically. Derived from the mood schedule: any slot that
 * contains MOOD_SLEEP counts as a sleep slot, so editing MOOD_SLOTS
 * automatically updates the sleep hours without a second source of truth.
 */
function isBangkokNight(): boolean {
  const hour = homeHour()
  const slot = MOOD_SLOTS.find((s) => hour >= s.start && hour < s.end)
  return slot?.moods.includes(MOOD_SLEEP) ?? false
}

// ============================================================================
// Pixel canvas
// ============================================================================

/**
 * Each sprite pixel is drawn as an N×N block in the canvas backing store,
 * so the canvas itself holds a true "pixel art" raster rather than relying
 * on CSS `image-rendering: pixelated` to do the upscaling at paint time.
 * 8× gives a 256×256 backing store — sharp on retina displays at 96 CSS px.
 */
const PIXEL_SCALE = 8

/**
 * Max pupil displacement from its rest position, in sprite pixels. ±1 is
 * the whole range a 1-pixel eye can move without leaving its socket — 9
 * discrete look directions (NW, N, NE, W, center, E, SW, S, SE).
 */
const EYE_MAX_OFFSET = 1

/**
 * Cursor distance (px) at which pupils reach full deflection. Smaller than
 * the head-tilt constant so the eyes commit to a direction sooner than the
 * head does — feels more alive.
 */
const EYE_SATURATION_DIST = 120

/** How often to attempt a blink (ms). Actual interval is randomized within. */
const BLINK_MIN_INTERVAL = 2200
const BLINK_MAX_INTERVAL = 5800
/** Blink duration (ms) — how long the eyes stay closed. */
const BLINK_DURATION = 110

// ---- Hair swing -------------------------------------------------------------
// The hair is a damped spring chasing the head's tilt. Its lag behind the
// head bends the strands: turn toward the cursor and the tips trail the
// other way, overshoot a touch, then settle back to rest.

/** Spring stiffness (1/s²) and damping (1/s): ζ ≈ 0.4, settles in under 1s. */
const HAIR_STIFFNESS = 120
const HAIR_DAMPING = 9

/**
 * Time constant (s) of the head's on-screen tilt. Approximates the 0.22s
 * ease-out CSS transition on `.bitboy-head-inner`, so the hair reacts to
 * where the head visibly is rather than where it's heading.
 */
const HEAD_FOLLOW_TAU = 0.07

/** Hair-to-head lag (deg) at which the strands reach their full bend. */
const HAIR_FULL_BEND_DEG = 2.5

/** Below this (deg and deg/s) the hair counts as settled and the loop stops. */
const HAIR_REST_EPSILON = 0.02

/** Quantize a continuous swing in [-1, 1] to the sprite's bend levels [-2, 2]. */
function toBend(swing: number): number {
  const level = Math.abs(swing) >= 0.6 ? 2 : Math.abs(swing) >= 0.3 ? 1 : 0
  return swing < 0 ? -level : level
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Cursor → head transform. Continuous in all directions (no sign-flip at the
 * center axis), with exponential distance saturation so the motion eases
 * naturally instead of clamping at a fixed radius.
 */
function calcHeadTransform(
  headX: number,
  headY: number,
  mouseX: number,
  mouseY: number
): { rotate: number; tx: number; ty: number } {
  const dx = mouseX - headX
  const dy = mouseY - headY
  const dist = Math.hypot(dx, dy)
  if (dist === 0) return { rotate: 0, tx: 0, ty: 0 }

  // 1 - e^(-d/τ): 0 at origin, ~0.63 at τ, ~0.86 at 2τ, asymptotes to 1.
  const factor = 1 - Math.exp(-dist / SATURATION_DIST)

  // Unit direction — smooth through all axes.
  const nx = dx / dist
  const ny = dy / dist

  return {
    rotate: nx * MAX_ROTATION * factor,
    tx: nx * MAX_TX * factor,
    ty: ny * MAX_TY * factor,
  }
}

/**
 * Owns every timer an effect schedules: `after` queues a callback (skipped
 * once stopped) and `stop` clears everything still pending. One teardown
 * releases all allocations — no dangling timers after unmount or re-run.
 */
function createTimerOwner() {
  let cancelled = false
  const timers = new Set<ReturnType<typeof setTimeout>>()

  const after = (fn: () => void, ms: number): void => {
    const id = setTimeout(() => {
      timers.delete(id)
      if (!cancelled) fn()
    }, ms)
    timers.add(id)
  }

  const stop = (): void => {
    cancelled = true
    for (const id of timers) clearTimeout(id)
    timers.clear()
  }

  return { after, stop }
}

/**
 * Pre-render every pixel that never moves (face and hair mass) into a
 * SPRITE_SIZE offscreen canvas, so each frame is one scaled draw plus the
 * handful of eye and strand pixels.
 */
function buildBaseCanvas(): HTMLCanvasElement | null {
  const base = document.createElement("canvas")
  base.width = SPRITE_SIZE
  base.height = SPRITE_SIZE

  const ctx = base.getContext("2d")
  if (!ctx) return null

  for (const p of BASE_PIXELS) {
    ctx.fillStyle = p.color
    ctx.fillRect(p.x, p.y, 1, 1)
  }
  return base
}

interface RenderState {
  readonly eyeOffsets: ReadonlyArray<{ dx: number; dy: number }>
  readonly blinking: boolean
  /** Quantized hair bend in [-2, 2]; negative bends strands to the left. */
  readonly hairBend: number
}

/**
 * Paint the sprite onto `canvas`: static base, then eyes, then hair strands
 * at their current bend. Nearest-neighbor throughout so every sprite pixel
 * remains an exact PIXEL_SCALE×PIXEL_SCALE block.
 */
function renderSprite(
  canvas: HTMLCanvasElement,
  base: HTMLCanvasElement,
  state: RenderState
) {
  const ctx = canvas.getContext("2d")
  if (!ctx) return

  const size = SPRITE_SIZE * PIXEL_SCALE
  ctx.imageSmoothingEnabled = false
  ctx.clearRect(0, 0, size, size)
  ctx.drawImage(base, 0, 0, SPRITE_SIZE, SPRITE_SIZE, 0, 0, size, size)

  const fill = (x: number, y: number, color: string) => {
    ctx.fillStyle = color
    ctx.fillRect(x * PIXEL_SCALE, y * PIXEL_SCALE, PIXEL_SCALE, PIXEL_SCALE)
  }

  // Eyes — open pupils or a "- -" closed dash depending on blink state.
  if (state.blinking) {
    for (const dash of EYES_CLOSED) {
      for (const p of dash) fill(p.x, p.y, EYE_COLOR)
    }
  } else {
    EYES.forEach((eye, i) => {
      const off = state.eyeOffsets[i] ?? { dx: 0, dy: 0 }
      fill(eye.x + off.dx, eye.y + off.dy, EYE_COLOR)
    })
  }

  // Hair strands — tips bend first, mid-strand pixels follow.
  for (const s of STRANDS) {
    fill(s.x + strandShift(s.flex, state.hairBend), s.y, s.color)
  }
}

/**
 * Compute the integer-pixel pupil offset for an eye given its on-screen
 * center and the cursor position. Same exponential-saturation curve as the
 * head tilt, but with a shorter saturation distance so eyes commit earlier.
 * The result is rounded to {-1, 0, +1} since pupils move on the pixel grid.
 */
function calcEyeOffset(
  eyeX: number,
  eyeY: number,
  mouseX: number,
  mouseY: number
): { dx: number; dy: number } {
  const dx = mouseX - eyeX
  const dy = mouseY - eyeY
  const dist = Math.hypot(dx, dy)
  if (dist === 0) return { dx: 0, dy: 0 }

  const factor = 1 - Math.exp(-dist / EYE_SATURATION_DIST)
  const mag = factor * EYE_MAX_OFFSET

  return {
    dx: Math.round((dx / dist) * mag),
    dy: Math.round((dy / dist) * mag),
  }
}

/**
 * Rest-position pupil offsets. The ref that holds this is only ever
 * *replaced* (never mutated in place), so one shared module-level array
 * is safe across remounts — and avoids rebuilding it on every render.
 */
const INITIAL_EYE_OFFSETS: ReadonlyArray<{ dx: number; dy: number }> = EYES.map(
  () => ({ dx: 0, dy: 0 })
)

/** Spring state for the hair swing, all angles in degrees. */
interface HairMotion {
  /** Tilt the head is heading toward (latest cursor target). */
  target: number
  /** Estimated on-screen head tilt, trailing `target` like the CSS transition. */
  head: number
  /** Where the hair "is" — the spring position chasing `head`. */
  hair: number
  /** Spring velocity (deg/s). */
  velocity: number
  /** rAF timestamp of the previous step, or null before the first. */
  lastTime: number | null
}

const restingHair = (): HairMotion => ({
  target: 0,
  head: 0,
  hair: 0,
  velocity: 0,
  lastTime: null,
})

export const BitBoyHead = memo(function BitBoyHead() {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const innerRef = useRef<HTMLSpanElement>(null)
  const baseRef = useRef<HTMLCanvasElement | null>(null)
  const eyeOffsetsRef = useRef<ReadonlyArray<{ dx: number; dy: number }>>(
    INITIAL_EYE_OFFSETS
  )
  const blinkingRef = useRef(false)
  const hairBendRef = useRef(0)
  const hairMotionRef = useRef<HairMotion>(restingHair())
  const hairRafRef = useRef<number | null>(null)
  const rafRef = useRef<number | null>(null)
  // Cursor-tilt is applied imperatively (rAF-coalesced style write) instead
  // of via React state, so mousemove never triggers a React re-render.
  const pendingTransformRef = useRef<{ rotate: number; tx: number; ty: number } | null>(null)
  const transformRafRef = useRef<number | null>(null)
  const [mood, setMood] = useState<AsciiMood | null>(null)
  const [frameIndex, setFrameIndex] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  // Initialized from the current Bangkok hour so first paint is already
  // correct (asleep vs. awake) — no flash of the wrong state on load.
  const [sleeping, setSleeping] = useState(() => isBangkokNight())
  // Shared page-wide pause (reduced motion pauses by default). Pressing the
  // head toggles it. Paused means fully still: no bubble cycling, no blinks,
  // no idle bob, no cursor tracking and no hair swing.
  const { paused, setPaused } = useAmbientMotion()

  // Poll every minute to catch hour rollovers (23:00 → asleep, 05:00 → awake).
  useEffect(() => {
    const id = setInterval(() => {
      setSleeping(isBangkokNight())
    }, 60_000)
    return () => clearInterval(id)
  }, [])

  const readRenderState = useCallback((): RenderState => ({
    eyeOffsets: eyeOffsetsRef.current,
    blinking: blinkingRef.current,
    hairBend: hairBendRef.current,
  }), [])

  /**
   * Schedule a canvas redraw on the next frame. Coalesces multiple mousemoves
   * (which can fire faster than rAF) into one paint per frame.
   */
  const requestRender = useCallback(() => {
    if (rafRef.current !== null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      const canvas = canvasRef.current
      const base = baseRef.current
      if (canvas && base) renderSprite(canvas, base, readRenderState())
    })
  }, [readRenderState])

  // Build the static base once, size the display canvas's backing store and
  // paint the first frame.
  useEffect(() => {
    const canvas = canvasRef.current
    const base = buildBaseCanvas()
    if (!canvas || !base) return

    baseRef.current = base
    const size = SPRITE_SIZE * PIXEL_SCALE
    canvas.width = size
    canvas.height = size
    renderSprite(canvas, base, readRenderState())

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [readRenderState])

  /**
   * Advance the hair spring one frame and redraw when the quantized bend
   * changes. Keeps scheduling itself until the hair settles, then stops —
   * no rAF loop runs while BitBoy is at rest.
   */
  const stepHair = useCallback(
    (now: number) => {
      const m = hairMotionRef.current
      const dt = m.lastTime === null ? 1 / 60 : Math.min((now - m.lastTime) / 1000, 1 / 30)
      m.lastTime = now

      m.head += (m.target - m.head) * (1 - Math.exp(-dt / HEAD_FOLLOW_TAU))
      const accel = HAIR_STIFFNESS * (m.head - m.hair) - HAIR_DAMPING * m.velocity
      m.velocity += accel * dt
      m.hair += m.velocity * dt

      const lag = m.hair - m.head
      const settled =
        Math.abs(m.target - m.head) < HAIR_REST_EPSILON &&
        Math.abs(lag) < HAIR_REST_EPSILON &&
        Math.abs(m.velocity) < HAIR_REST_EPSILON

      const swing = Math.max(-1, Math.min(1, lag / HAIR_FULL_BEND_DEG))
      const bend = settled ? 0 : toBend(swing)
      if (bend !== hairBendRef.current) {
        hairBendRef.current = bend
        requestRender()
      }

      if (settled) {
        hairMotionRef.current = { ...restingHair(), target: m.target, head: m.target, hair: m.target }
        hairRafRef.current = null
      } else {
        hairRafRef.current = requestAnimationFrame(stepHair)
      }
    },
    [requestRender]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const headCenterX = rect.left + rect.width / 2
      const headCenterY = rect.top + rect.height / 2

      // Queue the latest tilt; one rAF per frame consumes it, so bursts of
      // mousemove events collapse into a single style write — and React
      // never re-renders for cursor motion.
      const transform = calcHeadTransform(
        headCenterX,
        headCenterY,
        e.clientX,
        e.clientY
      )
      pendingTransformRef.current = transform
      if (transformRafRef.current === null) {
        transformRafRef.current = requestAnimationFrame(() => {
          transformRafRef.current = null
          const t = pendingTransformRef.current
          const el = innerRef.current
          if (t && el) {
            el.style.transform = `translate(${t.tx}px, ${t.ty}px) rotate(${t.rotate}deg)`
          }
        })
      }

      // Retarget the hair spring; wake its loop if the hair was at rest.
      hairMotionRef.current.target = transform.rotate
      if (hairRafRef.current === null) {
        hairMotionRef.current.lastTime = null
        hairRafRef.current = requestAnimationFrame(stepHair)
      }

      // Pupils follow the cursor independently per eye. Eye screen positions
      // are derived from the canvas's live bounding rect, so head tilt + idle
      // bob (which apply as CSS transforms) are naturally accounted for.
      const canvas = canvasRef.current
      if (!canvas || !baseRef.current) return
      const cRect = canvas.getBoundingClientRect()
      const scaleX = cRect.width / SPRITE_SIZE
      const scaleY = cRect.height / SPRITE_SIZE

      const next = EYES.map((eye) => {
        const eyeX = cRect.left + (eye.x + 0.5) * scaleX
        const eyeY = cRect.top + (eye.y + 0.5) * scaleY
        return calcEyeOffset(eyeX, eyeY, e.clientX, e.clientY)
      })

      // Avoid redraws when no pupil changed (they snap to ±1, so most
      // sub-pixel cursor movement is a no-op).
      const prev = eyeOffsetsRef.current
      const eyesChanged = next.some(
        (n, i) => n.dx !== prev[i]!.dx || n.dy !== prev[i]!.dy
      )
      if (eyesChanged) {
        eyeOffsetsRef.current = next
        requestRender()
      }
    },
    [requestRender, stepHair]
  )

  useEffect(() => {
    if (paused) {
      // Settle at rest: no tilt, centered pupils, hair hanging still.
      if (innerRef.current) innerRef.current.style.transform = ""
      eyeOffsetsRef.current = INITIAL_EYE_OFFSETS
      hairMotionRef.current = restingHair()
      hairBendRef.current = 0
      requestRender()
      return
    }

    window.addEventListener("mousemove", handleMouseMove, { passive: true })
    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      if (transformRafRef.current !== null) {
        cancelAnimationFrame(transformRafRef.current)
        transformRafRef.current = null
      }
      if (hairRafRef.current !== null) {
        cancelAnimationFrame(hairRafRef.current)
        hairRafRef.current = null
      }
    }
  }, [paused, handleMouseMove, requestRender])

  // Eye behavior:
  //   - asleep → eyes stay closed (blinkingRef pinned true, no scheduling)
  //   - paused → eyes stay open, no blinks
  //   - awake  → periodic randomized blinks
  // The effect re-runs whenever `sleeping` flips at an hour boundary, so
  // transitions (bedtime / wake-up) happen automatically.
  useEffect(() => {
    const { after, stop } = createTimerOwner()

    if (sleeping || paused) {
      blinkingRef.current = sleeping
      requestRender()
    } else {
      blinkingRef.current = false
      requestRender()

      const scheduleBlink = () => {
        const delay =
          BLINK_MIN_INTERVAL +
          Math.random() * (BLINK_MAX_INTERVAL - BLINK_MIN_INTERVAL)
        after(() => {
          blinkingRef.current = true
          requestRender()
          after(() => {
            blinkingRef.current = false
            requestRender()
            scheduleBlink()
          }, BLINK_DURATION)
        }, delay)
      }

      scheduleBlink()
    }

    return stop
  }, [sleeping, paused, requestRender])

  // Cycle moods: re-picks from the current Bangkok-hour slot each cycle
  // so the bubble naturally shifts between coffee / food / zzZ / etc. as
  // the day progresses. Avoids repeating the same mood twice in a row.
  // Pausing freezes the bubble where it is; resuming starts a new cycle.
  useEffect(() => {
    if (paused) return
    const { after, stop } = createTimerOwner()
    let last: AsciiMood | null = null

    const showNext = () => {
      const next = pickMood(last)
      last = next
      setMood(next)
      setFrameIndex(0)
      setIsVisible(true)

      after(() => {
        setIsVisible(false)
        after(showNext, THOUGHT_PAUSE)
      }, THOUGHT_DURATION)
    }

    after(showNext, 1500)
    return stop
  }, [paused])

  // Advance the ASCII frame while the bubble is visible. Only runs during
  // the 4s visible window — no wasted timers while the bubble is hidden.
  useEffect(() => {
    if (!mood || !isVisible || paused) return
    const id = setInterval(() => {
      setFrameIndex((i) => (i + 1) % mood.frames.length)
    }, FRAME_INTERVAL)
    return () => clearInterval(id)
  }, [mood, isVisible, paused])

  return (
    <div
      className="bitboy-container"
      ref={containerRef}
      data-paused={paused || undefined}
    >
      {/* Thought bubble — decorative, hidden from assistive technology */}
      <div
        className={`bitboy-thought ${isVisible ? "bitboy-thought--visible" : ""}`}
        aria-hidden="true"
      >
        <MoodFrame
          frame={mood ? mood.frames[frameIndex % mood.frames.length]! : ""}
        />
        <div className="bitboy-thought-tail">
          <div className="bitboy-thought-dot bitboy-thought-dot--1" />
          <div className="bitboy-thought-dot bitboy-thought-dot--2" />
        </div>
      </div>

      {/* Outer: autonomous idle bob (CSS keyframes).
          Inner: cursor-driven tilt (inline transform).
          Composing them on separate DOM nodes avoids the two animations
          fighting over the same transform. */}
      <button
        type="button"
        className="bitboy-toggle"
        aria-label="Pause animation"
        aria-pressed={paused}
        onClick={() => setPaused(!paused)}
      >
        <span className="bitboy-head">
          <span className="bitboy-head-inner" ref={innerRef}>
            <canvas ref={canvasRef} className="bitboy-img" aria-hidden="true" />
          </span>
        </span>
      </button>
    </div>
  )
})
